import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { listJobs } from '@/api/jobs';
import { getClassAnalytics, getJobSubmissionStatus } from '@/api/analytics';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import type { JobOut, JobSubmissionStatus } from '@/types';


import styles from './analytics.module.css';

/** 按布置日期（YYYY-MM-DD）分组 */
function groupByDate(jobs: JobOut[]): Map<string, JobOut[]> {
  const groups = new Map<string, JobOut[]>();
  for (const job of jobs) {
    const key = (job.created_at ?? '').slice(0, 10) || '未知日期';
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(job);
  }
  return groups;
}

function formatDateLabel(dateKey: string): string {
    if (dateKey === '未知日期') return dateKey;
    const d = new Date(`${dateKey}T00:00:00`);
    if (Number.isNaN(d.getTime())) return dateKey;
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const weekday = ['日', '一', '二', '三', '四', '五', '六'][d.getDay()];
    return `${month}月${day}日（周${weekday}）`;
}

const SUBMIT_STATUS_LABEL: Record<string, string> = {
  unsubmitted: '未提交',
  pending: '待批阅',
  grading: '批阅中',
  graded: '已初评',
  reviewed: '已复核',
};

type RosterFilter = 'all' | 'submitted' | 'unsubmitted' | 'graded';

const FILTER_LABEL: Record<RosterFilter, string> = {
  all: '全部',
  submitted: '已交',
  unsubmitted: '未交',
  graded: '已出分',
};

export function AnalyticsPage() {
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [dateFilter, setDateFilter] = useState('');
  const [rosterOpen, setRosterOpen] = useState(false);
  const [rosterFilter, setRosterFilter] = useState<RosterFilter>('all');


  const { data: jobsData } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => listJobs({ limit: 200 }),
  });

  // 按布置日期过滤 + 分组（新日期在前）
  const dateGroups = useMemo(() => {
    const jobs = (jobsData?.items ?? []).filter(
      (job) => !dateFilter || (job.created_at ?? '').slice(0, 10) === dateFilter,
    );
    const groups = [...groupByDate(jobs).entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
    return groups;
  }, [jobsData, dateFilter]);

  const { data, isLoading } = useQuery({
    queryKey: ['analytics', selectedJobId],
    queryFn: () => getClassAnalytics(selectedJobId!),
    enabled: !!selectedJobId,
  });

  const { data: submitStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['analytics', 'submission-status', selectedJobId],
    queryFn: () => getJobSubmissionStatus(selectedJobId!),
    enabled: !!selectedJobId,
  });

  const submitRate = submitStatus
    ? Math.round(submitStatus.submission_rate * 100)
    : 0;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>班级分析看板</h1>
          <p className={styles.subtitle}>按作业布置日期筛选，查看学情分析与提交情况</p>
        </div>
        <div className={styles.filterRow}>
          <label className={styles.dateFilter}>
            <span className={styles.dateFilterLabel}>布置日期</span>
            <input
              type="date"
              className={styles.dateInput}
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
            {dateFilter && (
              <button
                type="button"
                className={styles.dateClear}
                onClick={() => setDateFilter('')}
              >
                清除
              </button>
            )}
          </label>
          <select
            className={styles.select}
            value={selectedJobId ?? ''}
            onChange={(e) => setSelectedJobId(Number(e.target.value) || null)}
          >
            <option value="">选择作业</option>
            {dateGroups.map(([dateKey, jobs]) => (
              <optgroup key={dateKey} label={formatDateLabel(dateKey)}>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.title}
                    {job.subject ? `（${job.subject}）` : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </header>

      {selectedJobId && isLoading && <div className={styles.loading}>加载中...</div>}

      {selectedJobId && submitStatus && (
        <Card className={styles.submitCard}>
          <div className={styles.submitHead}>
            <h2 className={styles.sectionTitle}>
              提交情况
              {submitStatus.job.class_name && (
                <span className={styles.classNameTag}>{submitStatus.job.class_name}</span>
              )}
            </h2>
            <Button size="small" onClick={() => setRosterOpen(true)}>
              查看名单明细
            </Button>
          </div>

          <p className={styles.timeHint}>
            布置：{submitStatus.job.created_at ? new Date(submitStatus.job.created_at).toLocaleString() : '—'} · 截止：
            {submitStatus.job.due_date ? new Date(submitStatus.job.due_date).toLocaleString() : '未设置'}
          </p>

          <div className={styles.submitStats}>
            <button
              type="button"
              className={styles.submitStat}
              onClick={() => {
                setRosterFilter('all');
                setRosterOpen(true);
              }}
              aria-label="查看全部应交名单"
            >
              <span className={styles.submitStatValue}>{submitStatus.total_students}</span>
              <span className={styles.submitStatLabel}>应交人数</span>
            </button>
            <button
              type="button"
              className={`${styles.submitStat} ${styles.submitted}`}
              onClick={() => {
                setRosterFilter('submitted');
                setRosterOpen(true);
              }}
              aria-label="查看已提交名单"
            >
              <span className={styles.submitStatValue}>{submitStatus.submitted_count}</span>
              <span className={styles.submitStatLabel}>已提交</span>
            </button>
            <button
              type="button"
              className={`${styles.submitStat} ${styles.unsubmitted}`}
              onClick={() => {
                setRosterFilter('unsubmitted');
                setRosterOpen(true);
              }}
              aria-label="查看未提交名单"
            >
              <span className={styles.submitStatValue}>{submitStatus.unsubmitted_count}</span>
              <span className={styles.submitStatLabel}>未提交</span>
            </button>
            <button
              type="button"
              className={styles.submitStat}
              onClick={() => {
                setRosterFilter('graded');
                setRosterOpen(true);
              }}
              aria-label="查看已出分名单"
            >
              <span className={styles.submitStatValue}>{submitStatus.graded_count}</span>
              <span className={styles.submitStatLabel}>已出分</span>
            </button>
            <button
              type="button"
              className={styles.submitStat}
              onClick={() => {
                setRosterFilter('graded');
                setRosterOpen(true);
              }}
              aria-label="查看已出分名单（平均分计算来源）"
            >
              <span className={styles.submitStatValue}>
                {submitStatus.avg_score ?? '—'}
              </span>
              <span className={styles.submitStatLabel}>平均分</span>
            </button>
          </div>


          <div className={styles.rateWrap}>
            <div className={styles.rateBarTrack}>
              <div
                className={styles.rateBar}
                style={{ width: `${submitRate}%` }}
              />
            </div>
            <span className={styles.rateText}>提交率 {submitRate}%</span>
          </div>
          {statusLoading && <div className={styles.loading}>统计加载中...</div>}
        </Card>
      )}

      {data && (
        <div className={styles.grid}>
          <Card className={styles.card}>
            <h2 className={styles.sectionTitle}>共性错题排行</h2>
            <ul className={styles.mistakeList}>
              {data.top_mistakes.map((item) => (
                <li key={item.rank} className={styles.mistakeItem}>
                  <span className={styles.rank}>{item.rank}</span>
                  <span className={styles.name}>{item.name}</span>
                  <div className={styles.barWrap}>
                    <div
                      className={styles.bar}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          <Card className={styles.card}>
            <h2 className={styles.sectionTitle}>成绩分布</h2>
            <div className={styles.stats}>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>最高分</span>
                <span className={styles.statValue}>{data.distribution.max}</span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>平均分</span>
                <span className={styles.statValue}>{data.distribution.average}</span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>最低分</span>
                <span className={styles.statValue}>{data.distribution.min}</span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>中位数</span>
                <span className={styles.statValue}>{data.distribution.median}</span>
              </div>
            </div>
          </Card>

          <Card className={styles.card}>
            <h2 className={styles.sectionTitle}>薄弱知识点热力图</h2>
            <div className={styles.heatmap}>
              {data.heatmap.map((cell) => (
                <div
                  key={cell.name}
                  className={styles.heatCell}
                  style={{
                    backgroundColor: `rgba(255, 59, 48, ${cell.value})`,
                  }}
                >
                  {cell.name}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {!selectedJobId && (
        <div className={styles.empty}>请选择作业查看班级分析</div>
      )}
      {selectedJobId && !isLoading && !data && (
        <div className={styles.empty}>暂无分析数据</div>
      )}

      {/* 提交名单明细弹窗 */}
      {rosterOpen && submitStatus && (
        <RosterDialog
          submitStatus={submitStatus}
          filter={rosterFilter}
          onFilterChange={setRosterFilter}
          onClose={() => setRosterOpen(false)}
        />
      )}
    </div>
  );
}

interface RosterDialogProps {
  submitStatus: JobSubmissionStatus;
  filter: RosterFilter;
  onFilterChange: (filter: RosterFilter) => void;
  onClose: () => void;
}

function RosterDialog({ submitStatus, filter, onFilterChange, onClose }: RosterDialogProps) {
  const filteredItems = useMemo(() => {
    switch (filter) {
      case 'submitted':
        return submitStatus.items.filter((item) => item.submitted);
      case 'unsubmitted':
        return submitStatus.items.filter((item) => !item.submitted);
      case 'graded':
        return submitStatus.items.filter((item) => item.score !== null);
      default:
        return submitStatus.items;
    }
  }, [submitStatus.items, filter]);

  const emptyText: Record<RosterFilter, string> = {
    all: '该作业暂无班级应交名单（未关联班级或班级暂无学生）',
    submitted: '暂无已提交学生',
    unsubmitted: '暂无未提交学生',
    graded: '暂无已出分学生',
  };

  return (
    <div className={styles.dialogMask} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.dialogHead}>
          <div>
            <h3 className={styles.dialogTitle}>{submitStatus.job.title} · 提交名单</h3>
            <p className={styles.dialogMeta}>
              {submitStatus.job.class_name ? `${submitStatus.job.class_name} · ` : ''}
              布置日期{' '}
              {submitStatus.job.created_at
                ? new Date(submitStatus.job.created_at).toLocaleDateString()
                : '—'}
              {submitStatus.job.due_date
                ? ` · 截止 ${new Date(submitStatus.job.due_date).toLocaleString()}`
                : ''}
            </p>
          </div>
          <button
            type="button"
            className={styles.dialogClose}
            onClick={onClose}
            aria-label="关闭"
          >
            ×
          </button>
        </div>

        <div className={styles.rosterSummary}>
          {(['all', 'submitted', 'unsubmitted', 'graded'] as RosterFilter[]).map((key) => (
            <button
              key={key}
              type="button"
              className={`${styles.rosterTag} ${filter === key ? styles.rosterTagActive : ''} ${
                key === 'submitted'
                  ? styles.rosterTagGreen
                  : key === 'unsubmitted'
                    ? styles.rosterTagRed
                    : styles.rosterTagGray
              }`}
              onClick={() => onFilterChange(key)}
              aria-pressed={filter === key}
            >
              {FILTER_LABEL[key]} {key === 'all' ? submitStatus.total_students : key === 'submitted'
                ? submitStatus.submitted_count
                : key === 'unsubmitted'
                  ? submitStatus.unsubmitted_count
                  : submitStatus.graded_count}{' '}
              人
            </button>
          ))}
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>学生</th>
                <th>提交状态</th>
                <th>得分</th>
                <th>提交时间</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.student_id} className={item.submitted ? '' : styles.rowMiss}>
                  <td>
                    <span className={styles.studentName}>{item.display_name}</span>
                    <span className={styles.studentUsername}>@{item.username}</span>
                  </td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${
                        item.submitted ? styles.statusOk : styles.statusMiss
                      }`}
                    >
                      {SUBMIT_STATUS_LABEL[item.status] ?? item.status}
                    </span>
                  </td>
                  <td className={styles.scoreCell}>
                    {item.score !== null ? (
                      <>
                        <strong>{item.score}</strong>
                        <span className={styles.scoreMax}>/{item.max_score}</span>
                      </>
                    ) : (
                      <span className={styles.scoreEmpty}>—</span>
                    )}
                  </td>
                  <td className={styles.timeCell}>
                    {item.submitted_at
                      ? new Date(item.submitted_at).toLocaleString()
                      : '未提交'}
                  </td>
                </tr>
              ))}
              {filteredItems.length === 0 && (
                <tr>
                  <td colSpan={4} className={styles.empty}>
                    {emptyText[filter]}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

